import PageLayout from "components/layout/PageLayout"
import AvatarMini from "components/commons/AvatarMini"
import { useGetAttendancePresenceQuery, useGetRecentBadgeEventsQuery } from "./api/attendanceApiSlice"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { selectCompanyGuid } from "features/auth/authSlice"
import TopicSubscriber from "app/ws/TopicSubriber"
import { fromUnixTime, format, isToday, isYesterday } from "date-fns"
import { it, enUS } from "date-fns/locale"
import { useMemo } from "react"

const POLLING_INTERVAL = 30 * 1000

function elapsedLabel(epochTime) {
    const seconds = Math.max(0, Math.floor(Date.now() / 1000) - epochTime)
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (hours > 0) return `${hours}h ${minutes}min`
    if (minutes > 0) return `${minutes}min`
    return "adesso"
}

function formatEventTime(epochTime, lang) {
    const date = fromUnixTime(epochTime)
    const locale = lang === "it" ? it : enUS
    if (isToday(date)) return format(date, "HH:mm:ss")
    if (isYesterday(date)) return `Ieri ${format(date, "HH:mm")}`
    return format(date, "d MMM HH:mm", { locale })
}

function groupByBusinessUnit(events, unassignedLabel) {
    const map = new Map()
    events.forEach(event => {
        const key = event.businessUnit?.guid ?? "__unassigned__"
        if (!map.has(key)) {
            map.set(key, {
                guid: key,
                name: event.businessUnit?.name ?? unassignedLabel,
                events: []
            })
        }
        map.get(key).events.push(event)
    })
    return [...map.values()].sort((a, b) => {
        if (a.guid === "__unassigned__") return 1
        if (b.guid === "__unassigned__") return -1
        return a.name.localeCompare(b.name)
    })
}

function PresenceCard({ event, variant }) {
    const isInside = variant === "inside"
    const elapsed = elapsedLabel(event.epochTime)

    return (
        <div className={`d-flex align-items-center gap-2 px-2 py-1 rounded mb-1
            ${isInside ? "border border-success bg-success-subtle" : "border border-secondary"}`}>
            <span
                className="rounded-circle flex-shrink-0"
                style={{ width: 8, height: 8, background: isInside ? "#198754" : "#adb5bd" }}
            />
            <div className="flex-grow-1 overflow-hidden">
                {event.user
                    ? <AvatarMini guid={event.user.guid} displayName={event.user.displayName} abbreviate={true} size={28} />
                    : <span className="fw-semibold text-truncate d-block">{event.userGuid}</span>
                }
            </div>
            <span className="text-muted small text-nowrap flex-shrink-0">
                {isInside ? "da " : ""}{elapsed}{!isInside && " fa"}
            </span>
        </div>
    )
}

function PresencePanel({ presence, isFetching }) {
    const [t] = useTranslation(["attendance"])
    const { inside = [], outToday = [] } = presence ?? {}

    const insideGroups = useMemo(() => groupByBusinessUnit(inside, t("attendance:label.business_unit")), [inside, t])
    const outGroups = useMemo(() => groupByBusinessUnit(outToday, t("attendance:label.business_unit")), [outToday, t])

    return (
        <div className="row g-3 mb-4">
            <div className="col-md-6">
                <div className="card bg-dark h-100">
                    <div className="card-header d-flex align-items-center gap-2 py-2">
                        <span className="rounded-circle bg-success d-inline-block" style={{ width: 10, height: 10 }} />
                        <span className="fw-semibold">{t("attendance:label.inside")}</span>
                        <span className="badge bg-success ms-auto">{inside.length}</span>
                    </div>
                    <div className="card-body py-2 px-2" style={{ maxHeight: 300, overflowY: "auto" }}>
                        {inside.length === 0
                            ? <p className="text-muted small text-center my-3">{t("attendance:helper.no_one_inside")}</p>
                            : insideGroups.map(group => (
                                <div key={group.guid} className="mb-2">
                                    <div className="text-muted small fw-semibold px-1 mb-1" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                        <i className="fa-solid fa-building me-1" />{group.name}
                                    </div>
                                    {group.events.map(e => <PresenceCard key={e.guid} event={e} variant="inside" />)}
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>

            <div className="col-md-6">
                <div className="card bg-dark h-100">
                    <div className="card-header d-flex align-items-center gap-2 py-2">
                        <span className="rounded-circle bg-secondary d-inline-block" style={{ width: 10, height: 10 }} />
                        <span className="fw-semibold">{t("attendance:label.out_today")}</span>
                        <span className="badge bg-secondary ms-auto">{outToday.length}</span>
                    </div>
                    <div className="card-body py-2 px-2" style={{ maxHeight: 300, overflowY: "auto" }}>
                        {outToday.length === 0
                            ? <p className="text-muted small text-center my-3">{t("attendance:helper.no_one_out")}</p>
                            : outGroups.map(group => (
                                <div key={group.guid} className="mb-2">
                                    <div className="text-muted small fw-semibold px-1 mb-1" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                        <i className="fa-solid fa-building me-1" />{group.name}
                                    </div>
                                    {group.events.map(e => <PresenceCard key={e.guid} event={e} variant="out" />)}
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}

function BadgeEventRow({ event, lang }) {
    const isEntry = event.eventType === "ENTRY"
    return (
        <tr>
            <td>
                <span className={`badge ${isEntry ? "bg-success" : "bg-primary"}`}>
                    {isEntry ? "→" : "←"}
                </span>
            </td>
            <td>
                {event.user
                    ? <AvatarMini guid={event.user.guid} displayName={event.user.displayName} abbreviate={true} size={32} />
                    : <span className="fw-semibold text-muted">{event.userGuid}</span>
                }
            </td>
            <td className="text-muted small">
                {event.businessUnit?.name ?? <span className="text-muted">—</span>}
            </td>
            <td className="text-muted small">{formatEventTime(event.epochTime, lang)}</td>
            <td>
                {event.syncedAt != null && (
                    <span className="badge bg-warning text-dark">offline</span>
                )}
            </td>
        </tr>
    )
}

function AttendanceDashboard() {
    const [t, i18n] = useTranslation(["global", "attendance"])
    const companyGuid = useSelector(selectCompanyGuid)

    const {
        data: presence,
        isFetching: presenceFetching,
        refetch: refetchPresence
    } = useGetAttendancePresenceQuery(undefined, { pollingInterval: POLLING_INTERVAL })

    const {
        data: events = [],
        isFetching: eventsFetching,
        isLoading,
        isError,
        error,
        refetch: refetchEvents
    } = useGetRecentBadgeEventsQuery(30, { pollingInterval: POLLING_INTERVAL })

    const handleMqttMessage = () => {
        refetchPresence()
        refetchEvents()
    }

    const refetchAll = () => {
        refetchPresence()
        refetchEvents()
    }

    const content = (
        <>
            <TopicSubscriber
                subTopic={`company/${companyGuid}/attendance`}
                onMessageReceived={handleMqttMessage}
            />

            <PresencePanel presence={presence} isFetching={presenceFetching} />

            <h6 className="text-muted mb-2 d-flex align-items-center gap-2">
                <i className="fa-solid fa-clock-rotate-left" />
                {t("attendance:label.recent_events")}
                <span className="ms-auto small">
                    <i className="fa-solid fa-circle text-success me-1" style={{ fontSize: "0.5rem" }} />
                    {t("attendance:helper.realtime_hint")}
                </span>
            </h6>

            {events.length === 0 ? (
                <div className="text-center text-muted py-4">
                    <i className="fa-solid fa-clock-rotate-left fa-2x mb-3 d-block opacity-25" />
                    {t("attendance:helper.no_events")}
                </div>
            ) : (
                <table className="table table-hover table-sm align-middle">
                    <thead className="table-dark">
                        <tr>
                            <th style={{ width: "60px" }}>{t("attendance:label.event_type")}</th>
                            <th>{t("attendance:label.user")}</th>
                            <th>{t("attendance:label.business_unit")}</th>
                            <th>{t("attendance:label.date_time")}</th>
                            <th style={{ width: "80px" }} />
                        </tr>
                    </thead>
                    <tbody>
                        {events.map(event => (
                            <BadgeEventRow key={event.guid} event={event} lang={i18n.language} />
                        ))}
                    </tbody>
                </table>
            )}
        </>
    )

    return (
        <PageLayout
            pageTitle={t("attendance:menu.dashboard")}
            bodyContent={content}
            isLoadingPage={isLoading}
            isLoadingContent={eventsFetching || presenceFetching}
            isError={isError}
            errorMsg={error}
            onPageRefresh={refetchAll}
        />
    )
}

export default AttendanceDashboard
